/**
 * Formatting for a German cockpit.
 *
 * Everything the daemon stores is UTC, and everything shown here is local — that conversion
 * happens exactly here and nowhere else, so a screen can never mix the two. The only screen
 * that deliberately shows UTC is the flight detail's own line about it, which says so.
 */

const LOCALE = 'de-DE';

const timeFormat = new Intl.DateTimeFormat(LOCALE, {
  hour: '2-digit',
  minute: '2-digit',
});

const dateFormat = new Intl.DateTimeFormat(LOCALE, {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

const weekdayFormat = new Intl.DateTimeFormat(LOCALE, { weekday: 'short' });

export function localTime(t: number | null): string {
  if (t === null) return '–';
  return timeFormat.format(new Date(t * 1000));
}

export function localDate(t: number): string {
  return dateFormat.format(new Date(t * 1000));
}

export function weekday(t: number): string {
  return weekdayFormat.format(new Date(t * 1000));
}

export function utcTime(t: number | null): string {
  if (t === null) return '–';
  return `${new Date(t * 1000).toISOString().slice(11, 16)} UTC`;
}

/** `4:37` — hours and minutes, which is how a logbook counts. */
export function duration(seconds: number | null): string {
  if (seconds === null || !Number.isFinite(seconds)) return '–';
  const total = Math.max(0, Math.round(seconds));
  return `${Math.floor(total / 3600)}:${String(Math.floor((total % 3600) / 60)).padStart(2, '0')}`;
}

/** `2 h 14 min`, for a duration that is being read rather than summed. */
export function durationLong(seconds: number | null): string {
  if (seconds === null || !Number.isFinite(seconds)) return '–';
  const total = Math.max(0, Math.round(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  if (h === 0) return `${m} min`;
  return `${h} h ${String(m).padStart(2, '0')} min`;
}

export function number(value: number | null | undefined, digits = 1): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '–';
  return value.toLocaleString(LOCALE, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

export function km(value: number | null | undefined, digits = 1): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '–';
  return `${number(value, digits)} km`;
}

export function metres(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '–';
  return `${Math.round(value).toLocaleString(LOCALE)} m`;
}

export function kmh(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '–';
  return `${Math.round(value)} km/h`;
}

/** A climb rate always carries its sign: −2,1 m/s and +2,1 m/s are different news. */
export function climb(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '–';
  const sign = value > 0 ? '+' : '';
  return `${sign}${number(value, 1)} m/s`;
}

export function percent(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '–';
  return `${Math.round(value * 100)} %`;
}

export function place(name: string | null, code: string | null): string {
  if (code !== null && name !== null) return `${code} · ${name}`;
  return code ?? name ?? '–';
}

/** `vor 3 min`, for a live status that is only interesting while it is fresh. */
export function ago(seconds: number | null): string {
  if (seconds === null || !Number.isFinite(seconds)) return '–';
  if (seconds < 60) return `vor ${Math.round(seconds)} s`;
  if (seconds < 3600) return `vor ${Math.round(seconds / 60)} min`;
  return `vor ${Math.floor(seconds / 3600)} h ${Math.round((seconds % 3600) / 60)} min`;
}
